/**
 * The settings of each agent on each camera, which the cloud stores for each pair.
 *
 * No change frame names these settings, so a member loads when its first reader mounts, and
 * again after a gap in the stream. A write of this browser answers the member.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { makeKeyedFamily } from '../keyedFamily';
import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import type {
    AgentSettings, AgentSettingsKey, AgentSettingsSet, AgentSettingsSetKey,
    AgentSettingsSnapshot,
} from '@/types/agents';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import { SnapshotCommand } from '@/types/snapshot';

const PENDING: AgentSettingsSnapshot = { settings: null, isLoading: true, error: null };

/** One agent's settings on one camera: read it for the snapshot, set it with a command. */
export const agentSettingsAtom = makeKeyedFamily(
    ({ agentId, cameraId }: AgentSettingsKey) => snapshotMemberAtom(
        snapshotAtoms<AgentSettingsSnapshot>(
            PENDING,
            async (): Promise<AgentSettingsSnapshot> => ({
                settings: await api.getAgentDeviceSettings(agentId, cameraId),
                isLoading: false,
                error: null,
            }),
        ),
    ),
);

/**
 * A reload that only sets `isLoading` of a member keeps the set, so a held form sees no change.
 */
function isSameSet(left: AgentSettingsSet, right: AgentSettingsSet): boolean {
    const leftIds = Object.keys(left.byCamera);
    return left.isLoading === right.isLoading &&
        leftIds.length === Object.keys(right.byCamera).length &&
        leftIds.every((id) => left.byCamera[id] === right.byCamera[id]);
}

const sortedSetAtom = makeKeyedFamily(
    ({ agentId, cameraIds }: AgentSettingsSetKey) => selectAtom(
        atom((get): AgentSettingsSet => {
            const byCamera: Record<string, AgentSettings> = {};
            let isLoading = false;
            for (const cameraId of cameraIds) {
                const { settings, error } = get(agentSettingsAtom({ agentId, cameraId }));
                if (settings !== null) {
                    byCamera[cameraId] = settings;
                } else if (error === null) {
                    isLoading = true;
                }
            }
            return { byCamera, isLoading };
        }),
        (set): AgentSettingsSet => set,
        isSameSet,
    ),
);

/** One agent's settings on a set of cameras; the same set in any sequence is one atom. */
export function agentSettingsOfAtom(key: AgentSettingsSetKey): Atom<AgentSettingsSet> {
    return sortedSetAtom({ agentId: key.agentId, cameraIds: [...key.cameraIds].sort() });
}

/** Puts the answer of a write on screen, with no request. */
export const answerAgentSettingsAtom: WritableAtom<
    null, [key: AgentSettingsKey, settings: AgentSettings], void
> = atom(null, (_get, set, key: AgentSettingsKey, settings: AgentSettings): void => {
    set(agentSettingsAtom(key), {
        command: SnapshotCommand.Answer,
        snapshot: { settings, isLoading: false, error: null },
    });
});

const RELOAD = { command: SnapshotCommand.Reload } as const;

/** What the notifications stream means for the agent settings of the cameras. */
export const agentSettingsStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // A write of another session or of a media_server can be in the gap.
    if (input.kind !== StreamInputKind.Reopened) {
        return;
    }
    for (const key of agentSettingsAtom.getParams()) {
        set(agentSettingsAtom(key), RELOAD);
    }
});
